// Find My Part Filters - Client-side filtering logic
class FindMyPartFilters {
  constructor() {
    this.allProducts = []; // Store all products from API
    this.filteredProducts = []; // Store filtered products
    this.filters = {
      productLine: '',
      subCategory: '',
      productType: '',
      subModel: '',
      positions: []
    };

    // Filter elements
    this.productLineSelect = document.getElementById('filter-product-line');
    this.subCategorySelect = document.getElementById('filter-subcategory');
    this.productTypeSelect = document.getElementById('filter-product-type');
    this.subModelSelect = document.getElementById('filter-submodel');
    this.positionFiltersContainer = document.getElementById('position-filters');
    this.clearAllButton = document.getElementById('clear-all-filters');

    this.init();
  }

  init() {
    // Listen for products loaded event from search
    document.addEventListener('find-my-part:products-loaded', (e) => {
      console.log('[Filters] Products loaded:', e.detail);
      this.allProducts = e.detail.products;
      this.filteredProducts = [...this.allProducts];
      this.buildDynamicFilters();
    });

    // Setup filter event listeners
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Product Line filter
    if (this.productLineSelect) {
      this.productLineSelect.addEventListener('change', (e) => {
        this.filters.productLine = e.target.value;
        this.applyFilters();
      });
    }

    // SubCategory filter
    if (this.subCategorySelect) {
      this.subCategorySelect.addEventListener('change', (e) => {
        this.filters.subCategory = e.target.value;
        this.applyFilters();
      });
    }

    // Product Type filter
    if (this.productTypeSelect) {
      this.productTypeSelect.addEventListener('change', (e) => {
        this.filters.productType = e.target.value;
        this.applyFilters();
      });
    }

    // SubModel filter
    if (this.subModelSelect) {
      this.subModelSelect.addEventListener('change', (e) => {
        this.filters.subModel = e.target.value;
        this.applyFilters();
      });
    }

    // Clear all filters button
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  buildDynamicFilters() {
    if (!this.allProducts || this.allProducts.length === 0) return;

    console.log('[Filters] Building dynamic filters from products:', this.allProducts.length);

    // Extract unique values from product fields
    const productLines = new Set();
    const subCategories = new Set();
    const productTypes = new Set();
    const subModels = new Set();
    const positions = new Set();

    this.allProducts.forEach(product => {
      // Extract values from API response fields
      if (product.items_product_line) productLines.add(product.items_product_line);
      if (product.items_sub_category) subCategories.add(product.items_sub_category);
      if (product.items_part_type) productTypes.add(product.items_part_type);

      // SubModel can have multiple values separated by comma
      if (product.sub_model) {
        const subModelValues = product.sub_model.split(',').map(v => v.trim()).filter(v => v);
        subModelValues.forEach(sm => subModels.add(sm));
      }

      // Position can have multiple values separated by comma
      if (product.position) {
        const positionValues = product.position.split(',').map(v => v.trim()).filter(v => v);
        positionValues.forEach(p => positions.add(p));
      }
      if (product.items_position) {
        const positionValues = product.items_position.split(',').map(v => v.trim()).filter(v => v);
        positionValues.forEach(p => positions.add(p));
      }
    });

    console.log('[Filters] Extracted filter values:', {
      productLines: [...productLines],
      subCategories: [...subCategories],
      productTypes: [...productTypes],
      subModels: [...subModels],
      positions: [...positions]
    });

    // Build select dropdowns
    this.populateSelect(this.productLineSelect, [...productLines].sort());
    this.populateSelect(this.subCategorySelect, [...subCategories].sort());
    this.populateSelect(this.productTypeSelect, [...productTypes].sort());
    this.populateSelect(this.subModelSelect, [...subModels].sort());

    // Build position checkboxes
    this.buildPositionFilters([...positions].sort());
  }

  populateSelect(selectElement, values) {
    if (!selectElement) return;

    const filterGroup = selectElement.closest('.find-my-part__filter-group');

    // Keep the first "All" option and remove others
    while (selectElement.options.length > 1) {
      selectElement.remove(1);
    }

    // Add new options
    values.forEach(value => {
      if (value && value.trim() !== '') {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        selectElement.appendChild(option);
      }
    });

    // Hide/show the entire filter group based on whether there are values
    // Never show submodel filter group
    if (selectElement.id === 'filter-submodel') return;

    if (values.length === 0) {
      selectElement.disabled = true;
      if (filterGroup) {
        filterGroup.style.display = 'none';
      }
    } else {
      selectElement.disabled = false;
      if (filterGroup) {
        filterGroup.style.display = 'block';
      }
    }
  }

  buildPositionFilters(positions) {
    if (!this.positionFiltersContainer) return;

    const positionFilterGroup = this.positionFiltersContainer.closest('.find-my-part__filter-group');

    if (positions.length === 0) {
      // Hide the entire filter group if no positions
      if (positionFilterGroup) {
        positionFilterGroup.style.display = 'none';
      }
      return;
    }

    // Show the filter group if it was hidden
    if (positionFilterGroup) {
      positionFilterGroup.style.display = 'block';
    }

    this.positionFiltersContainer.innerHTML = '';

    positions.forEach((position, index) => {
      if (position && position.trim() !== '') {
        const label = document.createElement('label');
        label.className = 'filter-checkbox';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'position';
        checkbox.value = position;
        checkbox.id = `filter-position-${index}`;

        const span = document.createElement('span');
        span.textContent = position;

        label.appendChild(checkbox);
        label.appendChild(span);
        this.positionFiltersContainer.appendChild(label);

        // Add event listener
        checkbox.addEventListener('change', () => {
          this.updatePositionFilter();
          this.applyFilters();
        });
      }
    });
  }

  // Get available filter values based on current filter selections
  getAvailableFilterValues() {
    const availableValues = {
      productLines: new Set(),
      subCategories: new Set(),
      productTypes: new Set(),
      subModels: new Set(),
      positions: new Set()
    };

    // Filter products based on current selections, excluding one filter at a time
    const getFilteredProducts = (excludeFilter) => {
      return this.allProducts.filter(product => {
        // Product Line filter
        if (excludeFilter !== 'productLine' && this.filters.productLine && product.items_product_line !== this.filters.productLine) {
          return false;
        }

        // SubCategory filter
        if (excludeFilter !== 'subCategory' && this.filters.subCategory && product.items_sub_category !== this.filters.subCategory) {
          return false;
        }

        // Product Type filter
        if (excludeFilter !== 'productType' && this.filters.productType && product.items_part_type !== this.filters.productType) {
          return false;
        }

        // SubModel filter
        if (excludeFilter !== 'subModel' && this.filters.subModel) {
          if (!product.sub_model) return false;
          const productSubModels = product.sub_model.split(',').map(v => v.trim());
          if (!productSubModels.includes(this.filters.subModel)) {
            return false;
          }
        }

        // Position filter
        if (excludeFilter !== 'positions' && this.filters.positions.length > 0) {
          const productPosition = product.position || product.items_position;
          if (!productPosition) return false;

          const productPositions = productPosition.split(',').map(v => v.trim());
          const hasMatch = this.filters.positions.some(filterPos => productPositions.includes(filterPos));
          if (!hasMatch) {
            return false;
          }
        }

        return true;
      });
    };

    // Calculate available values for each filter
    const productLineProducts = getFilteredProducts('productLine');
    productLineProducts.forEach(product => {
      if (product.items_product_line) availableValues.productLines.add(product.items_product_line);
    });

    const subCategoryProducts = getFilteredProducts('subCategory');
    subCategoryProducts.forEach(product => {
      if (product.items_sub_category) availableValues.subCategories.add(product.items_sub_category);
    });

    const productTypeProducts = getFilteredProducts('productType');
    productTypeProducts.forEach(product => {
      if (product.items_part_type) availableValues.productTypes.add(product.items_part_type);
    });

    const subModelProducts = getFilteredProducts('subModel');
    subModelProducts.forEach(product => {
      if (product.sub_model) {
        const subModelValues = product.sub_model.split(',').map(v => v.trim()).filter(v => v);
        subModelValues.forEach(sm => availableValues.subModels.add(sm));
      }
    });

    const positionProducts = getFilteredProducts('positions');
    positionProducts.forEach(product => {
      const productPosition = product.position || product.items_position;
      if (productPosition) {
        const positionValues = productPosition.split(',').map(v => v.trim()).filter(v => v);
        positionValues.forEach(p => availableValues.positions.add(p));
      }
    });

    return {
      productLines: [...availableValues.productLines].sort(),
      subCategories: [...availableValues.subCategories].sort(),
      productTypes: [...availableValues.productTypes].sort(),
      subModels: [...availableValues.subModels].sort(),
      positions: [...availableValues.positions].sort()
    };
  }

  // Update filter options based on available values
  updateFilterOptions() {
    const available = this.getAvailableFilterValues();

    console.log('[Filters] Updating filter options with available values:', available);

    // Update Product Line select
    this.updateSelectWithAvailableValues(
      this.productLineSelect,
      available.productLines,
      this.filters.productLine
    );

    // Update SubCategory select
    this.updateSelectWithAvailableValues(
      this.subCategorySelect,
      available.subCategories,
      this.filters.subCategory
    );

    // Update Product Type select
    this.updateSelectWithAvailableValues(
      this.productTypeSelect,
      available.productTypes,
      this.filters.productType
    );

    // Update SubModel select
    this.updateSelectWithAvailableValues(
      this.subModelSelect,
      available.subModels,
      this.filters.subModel
    );

    // Update Position checkboxes
    this.updatePositionCheckboxes(available.positions);
  }

  // Update select element to show only available options
  updateSelectWithAvailableValues(selectElement, availableValues, currentValue) {
    if (!selectElement) return;

    const filterGroup = selectElement.closest('.find-my-part__filter-group');

    // Store the first option (the "All ..." option)
    const firstOption = selectElement.options[0];

    // Clear all options except the first
    while (selectElement.options.length > 1) {
      selectElement.remove(1);
    }

    // Add only available values
    availableValues.forEach(value => {
      if (value && value.trim() !== '') {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        selectElement.appendChild(option);
      }
    });

    // Restore current value if still available
    if (currentValue && availableValues.includes(currentValue)) {
      selectElement.value = currentValue;
    } else if (currentValue && !availableValues.includes(currentValue)) {
      // Current value is no longer available, reset to "All"
      selectElement.value = '';
    }

    // Hide/show filter group
    // Never show submodel filter group
    if (selectElement.id === 'filter-submodel') return;

    if (availableValues.length === 0) {
      selectElement.disabled = true;
      if (filterGroup) {
        filterGroup.style.display = 'none';
      }
    } else {
      selectElement.disabled = false;
      if (filterGroup) {
        filterGroup.style.display = 'block';
      }
    }
  }

  // Update position checkboxes to show only available positions
  updatePositionCheckboxes(availablePositions) {
    if (!this.positionFiltersContainer) return;

    const positionFilterGroup = this.positionFiltersContainer.closest('.find-my-part__filter-group');

    if (availablePositions.length === 0) {
      if (positionFilterGroup) {
        positionFilterGroup.style.display = 'none';
      }
      return;
    }

    if (positionFilterGroup) {
      positionFilterGroup.style.display = 'block';
    }

    // Get currently checked positions
    const checkedPositions = this.filters.positions;

    // Rebuild checkboxes with only available positions
    this.positionFiltersContainer.innerHTML = '';

    availablePositions.forEach((position, index) => {
      if (position && position.trim() !== '') {
        const label = document.createElement('label');
        label.className = 'filter-checkbox';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'position';
        checkbox.value = position;
        checkbox.id = `filter-position-${index}`;

        // Restore checked state if this position was previously selected
        if (checkedPositions.includes(position)) {
          checkbox.checked = true;
        }

        const span = document.createElement('span');
        span.textContent = position;

        label.appendChild(checkbox);
        label.appendChild(span);
        this.positionFiltersContainer.appendChild(label);

        // Add event listener
        checkbox.addEventListener('change', () => {
          this.updatePositionFilter();
          this.applyFilters();
        });
      }
    });
  }

  updatePositionFilter() {
    this.filters.positions = [];
    document.querySelectorAll('input[name="position"]:checked').forEach(checkbox => {
      this.filters.positions.push(checkbox.value);
    });
  }

  applyFilters() {
    console.log('[Filters] Applying filters:', this.filters);

    // Start with all products
    this.filteredProducts = this.allProducts.filter(product => {
      // Product Line filter (items_product_line)
      if (this.filters.productLine && product.items_product_line !== this.filters.productLine) {
        return false;
      }

      // SubCategory filter (items_sub_category)
      if (this.filters.subCategory && product.items_sub_category !== this.filters.subCategory) {
        return false;
      }

      // Product Type filter (items_part_type)
      if (this.filters.productType && product.items_part_type !== this.filters.productType) {
        return false;
      }

      // SubModel filter (sub_model) - check if selected submodel is in comma-separated list
      if (this.filters.subModel) {
        if (!product.sub_model) return false;
        const productSubModels = product.sub_model.split(',').map(v => v.trim());
        if (!productSubModels.includes(this.filters.subModel)) {
          return false;
        }
      }

      // Position filter (multi-select) - check if any selected position is in product's comma-separated list
      if (this.filters.positions.length > 0) {
        const productPosition = product.position || product.items_position;
        if (!productPosition) return false;

        const productPositions = productPosition.split(',').map(v => v.trim());
        const hasMatch = this.filters.positions.some(filterPos => productPositions.includes(filterPos));
        if (!hasMatch) {
          return false;
        }
      }

      return true;
    });

    console.log('[Filters] Filtered products:', this.filteredProducts.length, 'out of', this.allProducts.length);

    // Update filter options based on filtered results (dynamic filtering)
    this.updateFilterOptions();

    // Dispatch event with filtered products
    document.dispatchEvent(new CustomEvent('find-my-part:filters-applied', {
      detail: {
        products: this.filteredProducts,
        count: this.filteredProducts.length
      }
    }));

    // Update UI
    this.updateClearButtonVisibility();
  }

  clearAllFilters() {
    console.log('[Filters] Clearing all filters');

    // Reset filter values
    this.filters = {
      productLine: '',
      subCategory: '',
      productType: '',
      subModel: '',
      positions: []
    };

    // Reset select dropdowns
    if (this.productLineSelect) this.productLineSelect.value = '';
    if (this.subCategorySelect) this.subCategorySelect.value = '';
    if (this.productTypeSelect) this.productTypeSelect.value = '';
    if (this.subModelSelect) this.subModelSelect.value = '';

    // Uncheck all position checkboxes
    document.querySelectorAll('input[name="position"]').forEach(checkbox => {
      checkbox.checked = false;
    });

    // Reapply (show all products)
    this.applyFilters();
  }

  updateClearButtonVisibility() {
    if (!this.clearAllButton) return;

    const hasActiveFilters =
      this.filters.productLine !== '' ||
      this.filters.subCategory !== '' ||
      this.filters.productType !== '' ||
      this.filters.subModel !== '' ||
      this.filters.positions.length > 0;

    this.clearAllButton.style.display = hasActiveFilters ? 'block' : 'none';
  }

  hasActiveFilters() {
    return this.filters.productLine !== '' ||
      this.filters.subCategory !== '' ||
      this.filters.productType !== '' ||
      this.filters.subModel !== '' ||
      this.filters.positions.length > 0;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.findMyPartFilters = new FindMyPartFilters();
  });
} else {
  window.findMyPartFilters = new FindMyPartFilters();
}
